import React, { useEffect, useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import SwipeIcon from "@mui/icons-material/Swipe";
import { useTheme } from "../../../contexts/ThemeContext";

const normalizeHeader = (header, index) => {
  if (header && typeof header === "object") {
    return {
      label: header.label,
      key: header.key || `column-${index}`,
      sortable: header.sortable !== false,
      width: header.width,
    };
  }

  return {
    label: header,
    key: `column-${index}`,
    sortable: false,
    width: undefined,
  };
};

export const ProgressTable = ({ children, headers, sortConfig, onSort }) => {
  const theme = useTheme();
  const [showSwipeIndicators, setShowSwipeIndicators] = useState(true);
  const [isMobile, setIsMobile] = useState(false);


  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent;
      const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          ua,
        );
      const nextIsMobile = iOS || isMobileDevice;
      setIsMobile(nextIsMobile);

      if (nextIsMobile) {
        const timer = setTimeout(() => {
          setShowSwipeIndicators(false);
        }, 5000);

        return () => clearTimeout(timer);
      }

      return undefined;
    };

    return checkMobile();
  }, []);

  const headerBackground =
    theme.mode === "dark"
      ? theme.colors.backgroundPendienteLight
      : theme.colors.backgroundPendienteDark;
  const tableBackground =
    theme.mode === "dark"
      ? theme.colors?.backgroundColorNotCurrentModule
      : theme.colors?.background_secondary?.backgroundColor || "#FFFFFF";
  const sortedColor = "#FFFFFF";
  const mutedColor = theme.mode === "dark" ? "#B0B0B0" : "#757575";

  return (
    <div
      className="relative flex flex-col w-full h-full overflow-hidden bg-clip-border rounded-[8px]"
      style={{
        backgroundColor: tableBackground,
        boxShadow: theme.styles?.box_temas?.backgroundColor,
      }}
    >
      {isMobile && showSwipeIndicators && (
        <div
          className="absolute top-[2.2rem] left-0 right-0 flex justify-end px-4 z-10 pointer-events-none animate-pulse"
          style={{ transform: "translateY(-50%)" }}
          aria-hidden="true"
        >
          <SwipeIcon
            sx={{
              fontSize: "3.8rem",
              color: "#fff",
              fill: theme.mode === "dark" ? "#000" : "#fff",
              borderRadius: "50%",
              padding: "4px",
            }}
          />
        </div>
      )}

      <div
        className="w-full h-full overflow-auto shadow-xl rounded-[8px]"
        style={{ backgroundColor: tableBackground }}
      >
        <table className="w-full text-left table-auto min-w-[700px] laptop:min-w-0 border-collapse">
          <thead>
            <tr
              style={{
                backgroundColor: headerBackground,
                borderBottom:
                  theme.mode === "dark"
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid #e2e8f0",
              }}
            >
              {headers &&
                headers.map((header, idx) => {
                  const h = normalizeHeader(header, idx);
                  const sortable = h.sortable && typeof onSort === "function";
                  const isSorted = Boolean(sortConfig && sortConfig.key === h.key);
                  const direction = isSorted ? sortConfig.direction : null;
                  const headerColor = "#FFFFFF";

                  return (
                    <th
                      className={`p-4 border-none ${
                        sortable
                          ? "cursor-pointer select-none group transition-colors"
                          : ""
                      }`}
                      key={h.key || idx}
                      scope="col"
                      style={h.width ? { width: h.width, minWidth: h.width } : undefined}
                      onClick={sortable ? () => onSort(h.key) : undefined}
                      aria-sort={
                        sortable
                          ? isSorted
                            ? direction === "asc"
                              ? "ascending"
                              : "descending"
                            : "none"
                          : undefined
                      }
                      role={sortable ? "button" : undefined}
                      tabIndex={sortable ? 0 : undefined}
                      onKeyDown={
                        sortable
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSort(h.key);
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-center gap-1.5">
                        <p
                          className={`block text-[11px] leading-none font-medium uppercase transition-colors whitespace-nowrap ${
                            isSorted ? "font-bold" : ""
                          }`}
                          style={{ color: headerColor }}
                        >
                          {h.label}
                        </p>
                        {sortable && (
                          <span
                            className={`inline-flex items-center transition-all duration-150 ${
                              isSorted
                                ? "opacity-100 scale-100"
                                : "opacity-0 group-hover:opacity-40 scale-90"
                            }`}
                          >
                            {isSorted && direction === "desc" ? (
                              <ArrowDownwardIcon
                                sx={{
                                  fontSize: 14,
                                  color: sortedColor,
                                }}
                              />
                            ) : (
                              <ArrowUpwardIcon
                                sx={{
                                  fontSize: 14,
                                  color: isSorted ? sortedColor : mutedColor,
                                }}
                              />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
};
